"use client";

import React, { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { useAuth } from "@/lib/hooks/useAuth";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Card } from "@/components/ui/Card";
import { ArrowRight, CheckCircle2, Music, Mic, Radio, Headphones, FileMusic, Guitar, FileText, Users } from "lucide-react";
import toast from "react-hot-toast";
import { getFirebaseAuth } from "@/lib/api/firebase-ultra-safe";
import { INSTRUMENT_CATEGORIES } from "@/data/constants";
import { INSTRUMENT_SUBCATEGORIES } from "@/data/constants/subcategories";

export default function ExpertJoinFlow() {
  const router = useRouter();
  const { user, loading: authLoading } = useAuth();

  const [step, setStep] = useState<0 | 1 | 2 | "complete">(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Step 0: 서비스 선택
  const [selectedServices, setSelectedServices] = useState<string[]>([]);

  // Step 1: 기본 정보
  const [basicInfo, setBasicInfo] = useState({
    name: "",
    email: user?.email || "",
    phone: "",
    location: "",
  });

  // Step 2: 전문 정보
  const [expertInfo, setExpertInfo] = useState({
    specialties: [] as string[],
    instrumentSubCategory: [] as string[],
    customInstrument: "",
    experience: "",
    youtubeUrl: "",
    feedbackType: [] as string[],
  });

  const [expandedInstrumentCategory, setExpandedInstrumentCategory] = useState<string | null>(null);
  const [showCustomInstrumentInput, setShowCustomInstrumentInput] = useState(false);

  useEffect(() => {
    if (user?.email && !basicInfo.email) {
      setBasicInfo((prev) => ({ ...prev, email: user.email || "" }));
    }
  }, [user]);

  useEffect(() => {
    const checkExisting = async () => {
      if (authLoading || !user) return;
      try {
        const token = await getFirebaseAuth()?.currentUser?.getIdToken();
        if (!token) return;
        const res = await fetch("/api/instructor/apply", {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (data.pending) {
          setStep("complete");
        }
      } catch (error) {
        console.warn("프로 신청 상태 확인 실패:", error);
      }
    };
    void checkExisting();
  }, [user, authLoading]);

  const specialtyOptions = [
    { value: "보컬", icon: Mic, label: "보컬" },
    { value: "작곡", icon: Music, label: "작곡" },
    { value: "프로듀싱", icon: Radio, label: "프로듀싱" },
    { value: "믹싱", icon: Headphones, label: "믹싱" },
    { value: "마스터링", icon: FileMusic, label: "마스터링" },
    { value: "연주", icon: Guitar, label: "연주" },
  ];

  const playableInstrumentCategories = INSTRUMENT_CATEGORIES.filter(
    category => !["음향", "용품"].includes(category.key)
  );

  const handleBasicInfoChange = (field: string, value: string) => {
    setBasicInfo((prev) => ({ ...prev, [field]: value }));
  };

  const handleExpertInfoChange = (field: string, value: string | string[]) => {
    setExpertInfo((prev) => ({ ...prev, [field]: value }));
  };

  const handleSpecialtyToggle = (specialty: string) => {
    setExpertInfo((prev) => {
      const current = prev.specialties;
      const isSelected = current.includes(specialty);
      const updated = isSelected
        ? current.filter((s) => s !== specialty)
        : [...current, specialty];

      // 연주 선택/해제 처리
      if (specialty === "연주") {
        if (!isSelected) {
          if (!expandedInstrumentCategory && playableInstrumentCategories.length > 0) {
            setExpandedInstrumentCategory(playableInstrumentCategories[0].key);
          }
        } else {
          setShowCustomInstrumentInput(false);
          setExpandedInstrumentCategory(null);
          return {
            ...prev,
            specialties: updated,
            instrumentSubCategory: [],
            customInstrument: "",
          };
        }
      }

      return { ...prev, specialties: updated };
    });
  };

  const handleFeedbackTypeToggle = (feedbackType: string) => {
    setExpertInfo((prev) => {
      const current = prev.feedbackType;
      const isSelected = current.includes(feedbackType);
      const updated = isSelected
        ? current.filter((f) => f !== feedbackType)
        : [...current, feedbackType];
      return { ...prev, feedbackType: updated };
    });
  };

  const handleInstrumentSubCategoryToggle = (subcategory: string) => {
    setExpertInfo((prev) => {
      const current = prev.instrumentSubCategory;
      const isSelected = current.includes(subcategory);
      const updated = isSelected
        ? current.filter((c) => c !== subcategory)
        : [...current, subcategory];
      return { ...prev, instrumentSubCategory: updated };
    });
  };

  const handleInstrumentCategoryToggle = (categoryKey: string) => {
    setExpandedInstrumentCategory((prev) =>
      prev === categoryKey ? null : categoryKey
    );
  };

  const handleCustomInstrumentToggle = () => {
    setShowCustomInstrumentInput((prev) => {
      const next = !prev;
      setExpertInfo((prevInfo) => {
        const hasCustom = prevInfo.instrumentSubCategory.includes("기타(직접 입력)");
        let updatedSubCategories = prevInfo.instrumentSubCategory;

        if (next && !hasCustom) {
          updatedSubCategories = [...updatedSubCategories, "기타(직접 입력)"];
        } else if (!next && hasCustom) {
          updatedSubCategories = updatedSubCategories.filter(
            (c) => c !== "기타(직접 입력)"
          );
        }

        return {
          ...prevInfo,
          instrumentSubCategory: updatedSubCategories,
          customInstrument: next ? prevInfo.customInstrument : "",
        };
      });
      return next;
      });
  };

  const handleServiceToggle = (service: string) => {
    setSelectedServices((prev) =>
      prev.includes(service)
        ? prev.filter((s) => s !== service)
        : [...prev, service]
    );
  };

  const validateStep0 = (): boolean => {
    if (selectedServices.length === 0) {
      toast.error("최소 1개 이상의 서비스를 선택해주세요.");
      return false;
    }
    if (selectedServices.includes("feedback") && expertInfo.feedbackType.length === 0) {
      toast.error("피드백 서비스를 선택하셨다면 최소 1개 이상의 피드백 타입을 선택해주세요.");
      return false;
    }
    return true;
  };

  const validateStep1 = (): boolean => {
    if (!basicInfo.name.trim()) {
      toast.error("이름을 입력해주세요.");
      return false;
    }
    if (!basicInfo.phone.trim()) {
      toast.error("연락처를 입력해주세요.");
      return false;
    }
    if (!basicInfo.location.trim()) {
      toast.error("활동 지역을 선택해주세요.");
      return false;
    }
    return true;
  };

  const validateStep2 = (): boolean => {
    if (expertInfo.specialties.length === 0) {
      toast.error("최소 1개 이상의 전문 분야를 선택해주세요.");
      return false;
    }
    if (expertInfo.specialties.includes("연주") && expertInfo.instrumentSubCategory.length === 0 && !expertInfo.customInstrument.trim()) {
      toast.error("연주 악기를 선택하거나 직접 입력해주세요.");
      return false;
    }
    if (!expertInfo.experience.trim()) {
      toast.error("경력을 입력해주세요.");
      return false;
    }
    return true;
  };

  const handleNext = () => {
    if (step === 0) {
      if (validateStep0()) {
        setStep(1);
      }
    } else if (step === 1) {
      if (validateStep1()) {
        setStep(2);
      }
    }
  };

  const handleSubmit = async () => {
    if (!validateStep2() || !user) {
      return;
    }

    setIsSubmitting(true);

    try {
      // 연주 악기 정보 정리
      let instrumentInfo: string[] = [];
      if (expertInfo.specialties.includes("연주")) {
        instrumentInfo = [...expertInfo.instrumentSubCategory];
        if (expertInfo.customInstrument.trim()) {
          instrumentInfo.push(expertInfo.customInstrument.trim());
        }
      }

      const token = await getFirebaseAuth()?.currentUser?.getIdToken();
      if (!token) {
        toast.error("로그인이 필요합니다. 다시 로그인해 주세요.");
        return;
      }

      const response = await fetch("/api/instructor/apply", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: basicInfo.name,
          email: basicInfo.email,
          phone: basicInfo.phone,
          location: basicInfo.location,
          specialty: expertInfo.specialties,
          instrumentSubCategory: instrumentInfo,
          experience: expertInfo.experience,
          youtubeUrl: expertInfo.youtubeUrl.trim() || "",
          services: selectedServices,
          feedbackType: expertInfo.feedbackType,
        }),
      });

      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        toast.error(
          typeof result.error === "string"
            ? result.error
            : "프로 전환 요청에 실패했습니다."
        );
        return;
      }

      toast.success("프로 전환 요청이 접수되었습니다!");
      setStep("complete");
    } catch (error) {
      console.error("가입 실패:", error);
      toast.error("가입 중 오류가 발생했습니다.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoToProfile = () => {
    router.push("/profile");
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#A1E3D8] mx-auto mb-4"></div>
          <p className="text-gray-600">로딩 중...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    router.push("/auth/login?redirect=/instructor/register");
    return null;
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA] py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* 진행 표시기 */}
        {step !== "complete" && (
          <div className="mb-8">
            <div className="flex items-center justify-center gap-2 mb-4">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                  step >= 0
                    ? "bg-[#A1E3D8] text-gray-900"
                    : "bg-gray-200 text-gray-500"
                }`}
              >
                {step > 0 ? <CheckCircle2 className="w-5 h-5" /> : "1"}
              </div>
              <div
                className={`h-1 w-20 transition-colors ${
                  step >= 1 ? "bg-[#A1E3D8]" : "bg-gray-200"
                }`}
              />
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                  step >= 1
                    ? "bg-[#A1E3D8] text-gray-900"
                    : "bg-gray-200 text-gray-500"
                }`}
              >
                {step > 1 ? <CheckCircle2 className="w-5 h-5" /> : "2"}
              </div>
              <div
                className={`h-1 w-20 transition-colors ${
                  step >= 2 ? "bg-[#A1E3D8]" : "bg-gray-200"
                }`}
              />
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                  step >= 2
                    ? "bg-[#A1E3D8] text-gray-900"
                    : "bg-gray-200 text-gray-500"
                }`}
              >
                {step > 2 ? <CheckCircle2 className="w-5 h-5" /> : "3"}
              </div>
            </div>
          </div>
        )}

        <AnimatePresence mode="wait">
          {/* Step 0: 서비스 선택 */}
          {step === 0 && (
            <motion.div
              key="step0"
              initial={{ opacity: 0, x: 100 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -100 }}
              transition={{ duration: 0.3 }}
            >
              <Card className="bg-white rounded-2xl shadow-sm p-8">
                <div className="mb-8">
                  <h1 className="text-2xl font-bold text-gray-900 mb-2">
                    프로필 등록
                  </h1>
                  <p className="text-sm text-gray-500">
                    제공하실 서비스를 선택해주세요. 중복 선택이 가능합니다.
                  </p>
                </div>

                <div className="space-y-4 mb-8">
                  {/* 피드백 서비스 */}
                  <button
                    type="button"
                    onClick={() => handleServiceToggle("feedback")}
                    className={`w-full p-6 rounded-xl border-2 transition-all text-left ${
                      selectedServices.includes("feedback")
                        ? "border-[#A1E3D8] bg-[#A1E3D8]/20 shadow-lg ring-2 ring-[#A1E3D8]/30"
                        : "border-gray-200 hover:border-gray-300 bg-white"
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <div
                        className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${
                          selectedServices.includes("feedback")
                            ? "bg-[#A1E3D8] shadow-md scale-110"
                            : "bg-gray-100"
                        }`}
                      >
                        <FileText
                          className={`w-6 h-6 transition-all ${
                            selectedServices.includes("feedback")
                              ? "text-gray-900 font-bold"
                              : "text-gray-600"
                          }`}
                        />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className={`text-lg font-semibold transition-all ${
                            selectedServices.includes("feedback")
                              ? "text-gray-900 font-bold"
                              : "text-gray-900"
                          }`}>
                            프로 피드백
                          </h3>
                          {selectedServices.includes("feedback") && (
                            <CheckCircle2 className="w-6 h-6 text-[#A1E3D8] fill-[#A1E3D8] animate-in zoom-in duration-300" />
                          )}
                        </div>
                        <p className={`text-sm leading-relaxed transition-all ${
                          selectedServices.includes("feedback")
                            ? "text-gray-700 font-medium"
                            : "text-gray-600"
                        }`}>
                          학생의 음악 작품을 분석하고 전문적인 피드백 리포트를 작성합니다.
                          <br />
                          PDF 리포트, 실시간 피드백 등 다양한 형태로 제공 가능합니다.
                        </p>
                      </div>
                    </div>
                  </button>

                  {/* 피드백 타입 선택 */}
                  {selectedServices.includes("feedback") && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                      className="mt-4 rounded-2xl border border-[#A1E3D8]/40 bg-[#F8FBFF] px-4 py-4 sm:px-5 sm:py-5"
                    >
                      <p className="text-sm font-semibold text-gray-900 mb-3">
                        제공 가능한 피드백 타입을 선택해주세요 (복수 선택 가능)
                      </p>
                      <div className="flex flex-wrap gap-3">
                        {[
                          { value: "PDF 리포트", label: "PDF 리포트" },
                          { value: "실시간 피드백", label: "실시간 피드백" },
                          { value: "모두 가능", label: "모두 가능" },
                        ].map((option) => {
                          const isSelected = expertInfo.feedbackType.includes(option.value);
                          return (
                            <button
                              key={option.value}
                              type="button"
                              onClick={() => handleFeedbackTypeToggle(option.value)}
                              className={`rounded-full px-4 py-2 text-sm font-medium transition-all flex items-center gap-2 ${
                                isSelected
                                  ? "bg-[#A1E3D8] text-gray-900 shadow-sm"
                                  : "bg-white text-gray-700 hover:bg-gray-100 border border-gray-200"
                              }`}
                            >
                              {option.label}
                              {isSelected && (
                                <CheckCircle2 className="w-4 h-4 fill-current animate-in zoom-in duration-200" />
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}

                  {/* 레슨 서비스 */}
                  <button
                    type="button"
                    onClick={() => handleServiceToggle("lesson")}
                    className={`w-full p-6 rounded-xl border-2 transition-all text-left ${
                      selectedServices.includes("lesson")
                        ? "border-[#C9DFF8] bg-[#C9DFF8]/20 shadow-lg ring-2 ring-[#C9DFF8]/30"
                        : "border-gray-200 hover:border-gray-300 bg-white"
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <div
                        className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${
                          selectedServices.includes("lesson")
                            ? "bg-[#C9DFF8] shadow-md scale-110"
                            : "bg-gray-100"
                        }`}
                      >
                        <Users
                          className={`w-6 h-6 transition-all ${
                            selectedServices.includes("lesson")
                              ? "text-gray-900 font-bold"
                              : "text-gray-600"
                          }`}
                        />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className={`text-lg font-semibold transition-all ${
                            selectedServices.includes("lesson")
                              ? "text-gray-900 font-bold"
                              : "text-gray-900"
                          }`}>
                            레슨 제공
                          </h3>
                          {selectedServices.includes("lesson") && (
                            <CheckCircle2 className="w-6 h-6 text-[#C9DFF8] fill-[#C9DFF8] animate-in zoom-in duration-300" />
                          )}
                        </div>
                        <p className={`text-sm leading-relaxed transition-all ${
                          selectedServices.includes("lesson")
                            ? "text-gray-700 font-medium"
                            : "text-gray-600"
                        }`}>
                          1:1 개인 레슨, 정기 레슨, 프로 집중 레슨 등을 제공합니다.
                          <br />
                          온라인/오프라인 모두 가능하며, 학생과 직접 일정을 조율할 수 있습니다.
                        </p>
                      </div>
                    </div>
                  </button>
                </div>

                <div className="mt-8 flex justify-end">
                  <Button
                    onClick={handleNext}
                    className="rounded-full h-12 text-base font-medium bg-[#A1E3D8] hover:bg-[#8DD3C7] text-gray-900 px-8 flex items-center gap-2"
                  >
                    다음으로
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </div>
              </Card>
            </motion.div>
          )}

          {/* Step 1: 기본 정보 */}
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 100 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -100 }}
              transition={{ duration: 0.3 }}
            >
              <Card className="bg-white rounded-2xl shadow-sm p-8">
                <div className="mb-8">
                  <h1 className="text-2xl font-bold text-gray-900 mb-2">
                    기본 정보 입력
                  </h1>
                  <p className="text-sm text-gray-500">
                    기본 정보를 입력해주세요.
                  </p>
                </div>

                <div className="space-y-6">
                  <div>
                    <label className="block text-sm text-gray-500 mb-2">
                      이름 <span className="text-red-500">*</span>
                    </label>
                    <Input
                      type="text"
                      value={basicInfo.name}
                      onChange={(e) => handleBasicInfoChange("name", e.target.value)}
                      placeholder="홍길동"
                      className="rounded-xl border-gray-200 focus:border-gray-400"
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-gray-500 mb-2">
                      이메일
                    </label>
                    <Input
                      type="email"
                      value={basicInfo.email}
                      disabled
                      className="rounded-xl border-gray-200 bg-gray-50 text-gray-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-gray-500 mb-2">
                      연락처 <span className="text-red-500">*</span>
                    </label>
                    <Input
                      type="tel"
                      value={basicInfo.phone}
                      onChange={(e) => handleBasicInfoChange("phone", e.target.value)}
                      placeholder="010-1234-5678"
                      className="rounded-xl border-gray-200 focus:border-gray-400"
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-gray-500 mb-2">
                      활동 지역 <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={basicInfo.location}
                      onChange={(e) => handleBasicInfoChange("location", e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-gray-400 focus:outline-none focus:ring-2 focus:ring-[#A1E3D8]/20 bg-white text-gray-900 transition-colors"
                    >
                      <option value="">활동 지역을 선택해주세요</option>
                      <option value="서울">서울</option>
                      <option value="경기">경기</option>
                      <option value="인천">인천</option>
                      <option value="부산">부산</option>
                      <option value="대구">대구</option>
                      <option value="광주">광주</option>
                      <option value="대전">대전</option>
                      <option value="울산">울산</option>
                      <option value="세종">세종</option>
                      <option value="강원">강원</option>
                      <option value="충북">충북</option>
                      <option value="충남">충남</option>
                      <option value="전북">전북</option>
                      <option value="전남">전남</option>
                      <option value="경북">경북</option>
                      <option value="경남">경남</option>
                      <option value="제주">제주</option>
                      <option value="온라인">온라인</option>
                    </select>
                  </div>
                </div>

                <div className="mt-8 flex justify-end">
                  <Button
                    onClick={handleNext}
                    className="rounded-full h-12 text-base font-medium bg-[#A1E3D8] hover:bg-[#8DD3C7] text-gray-900 px-8 flex items-center gap-2"
                  >
                    다음으로
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </div>
              </Card>
            </motion.div>
          )}

          {/* Step 2: 전문 정보 */}
          {step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 100 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -100 }}
              transition={{ duration: 0.3 }}
            >
              <Card className="bg-white rounded-2xl shadow-sm p-8">
                <div className="mb-8">
                  <h1 className="text-2xl font-bold text-gray-900 mb-2">
                    전문 정보 입력
                  </h1>
                  <p className="text-sm text-gray-500">
                    당신의 전문 분야를 선택하고 간단히 소개해주세요.
                  </p>
                </div>

                <div className="space-y-6">
                  <div>
                    <label className="block text-sm text-gray-500 mb-2">
                      전문 분야 <span className="text-red-500">*</span> (최소 1개 선택)
                    </label>
                    <div className="flex flex-wrap gap-3">
                      {specialtyOptions.map((option) => {
                        const Icon = option.icon;
                        const isSelected = expertInfo.specialties.includes(option.value);
                        return (
                          <button
                            key={option.value}
                            type="button"
                            onClick={() => handleSpecialtyToggle(option.value)}
                            className={`rounded-full px-4 py-2 text-sm font-medium transition-all flex items-center gap-2 ${
                              isSelected
                                ? "bg-[#A1E3D8] text-gray-900"
                                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                            }`}
                          >
                            <Icon className="w-4 h-4" />
                            {option.label}
                          </button>
                        );
                      })}
                    </div>

                    {/* 연주 하위 카테고리 */}
                    {expertInfo.specialties.includes("연주") && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                        className="mt-4 space-y-4 rounded-2xl border border-[#A1E3D8]/40 bg-[#F8FBFF] px-4 py-4 sm:px-5 sm:py-5"
                      >
                        <div>
                          <p className="text-xs font-semibold text-[#4A5D8C] mb-3">
                            연주 악기 대분류 선택
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {playableInstrumentCategories.map((category) => {
                              const isActive = expandedInstrumentCategory === category.key;
                              return (
                                <button
                                  key={category.key}
                                  type="button"
                                  onClick={() => handleInstrumentCategoryToggle(category.key)}
                                  className={`rounded-full px-3 py-1.5 text-xs font-medium transition-all ${
                                    isActive
                                      ? "bg-[#A1E3D8] text-gray-900 shadow-sm"
                                      : "bg-white text-gray-600 hover:bg-gray-100 border border-gray-200"
                                  }`}
                                >
                                  {category.label}
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {expandedInstrumentCategory && (
                          <div className="space-y-3">
                            <p className="text-xs text-gray-500">
                              세부 악기를 선택해주세요 (복수 선택 가능)
                        </p>
                        <div className="flex flex-wrap gap-2">
                              {(INSTRUMENT_SUBCATEGORIES[
                                expandedInstrumentCategory as keyof typeof INSTRUMENT_SUBCATEGORIES
                              ] || []).map((subcategory) => {
                                const isSelected = expertInfo.instrumentSubCategory.includes(
                                  subcategory.label
                                );
                            return (
                              <button
                                    key={subcategory.key}
                                type="button"
                                    onClick={() => handleInstrumentSubCategoryToggle(subcategory.label)}
                                className={`rounded-full px-3 py-1.5 text-xs font-medium transition-all ${
                                  isSelected
                                        ? "bg-[#C9DFF8] text-gray-900 shadow-sm"
                                        : "bg-white text-gray-600 hover:bg-gray-100 border border-gray-200"
                                }`}
                              >
                                    {subcategory.label}
                              </button>
                            );
                          })}
                          <button
                            type="button"
                                onClick={handleCustomInstrumentToggle}
                            className={`rounded-full px-3 py-1.5 text-xs font-medium transition-all ${
                              showCustomInstrumentInput
                                    ? "bg-[#C9DFF8] text-gray-900 shadow-sm"
                                    : "bg-white text-gray-600 hover:bg-gray-100 border border-gray-200"
                            }`}
                          >
                            기타(직접 입력)
                          </button>
                        </div>

                        {showCustomInstrumentInput && (
                          <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                                className="mt-2"
                          >
                            <Input
                              type="text"
                              value={expertInfo.customInstrument}
                              onChange={(e) =>
                                handleExpertInfoChange("customInstrument", e.target.value)
                              }
                                  placeholder="직접 입력할 악기를 적어주세요"
                              className="rounded-xl border-gray-200 focus:border-gray-400"
                            />
                          </motion.div>
                            )}
                          </div>
                        )}
                      </motion.div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm text-gray-500 mb-2">
                      경력 <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={expertInfo.experience}
                      onChange={(e) => handleExpertInfoChange("experience", e.target.value)}
                      placeholder="예: 10년 이상 작곡 경력, 다수의 OST 제작 참여. 주요 작품으로는...&#10;&#10;자유롭게 경력과 활동 내역을 작성해주세요."
                      rows={6}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-gray-400 focus:outline-none focus:ring-2 focus:ring-[#A1E3D8]/20 bg-white text-gray-900 transition-colors resize-none"
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      자유롭게 경력과 활동 내역을 작성해주세요.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm text-gray-500 mb-2">
                      유튜브 링크 (선택)
                    </label>
                    <Input
                      type="url"
                      value={expertInfo.youtubeUrl}
                      onChange={(e) => handleExpertInfoChange("youtubeUrl", e.target.value)}
                      placeholder="https://youtube.com/@..."
                      className="rounded-xl border-gray-200 focus:border-gray-400"
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      유튜브 채널이나 작품 링크를 입력해주세요.
                    </p>
                  </div>
                </div>

                <div className="mt-8 flex justify-between">
                  <Button
                    onClick={() => setStep(1)}
                    variant="outline"
                    className="rounded-full h-12 text-base font-medium"
                  >
                    이전으로
                  </Button>
                  <Button
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    loading={isSubmitting}
                    className="rounded-full h-12 text-base font-medium bg-[#A1E3D8] hover:bg-[#8DD3C7] text-gray-900 px-8 flex items-center gap-2"
                  >
                    프로 전환 요청하기
                  </Button>
                </div>
              </Card>
            </motion.div>
          )}

          {/* 완료 화면 */}
          {step === "complete" && (
            <motion.div
              key="complete"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4 }}
            >
              <Card className="bg-white rounded-2xl shadow-sm p-12 text-center">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                  className="w-20 h-20 bg-[#A1E3D8] rounded-full flex items-center justify-center mx-auto mb-6"
                >
                  <CheckCircle2 className="w-10 h-10 text-gray-900" />
                </motion.div>

                <h1 className="text-3xl font-bold text-gray-900 mb-4">
                  프로 전환 요청이 접수되었습니다
                </h1>
                <p className="text-gray-600 mb-8">
                  관리자 승인 후 프로 기능을 이용할 수 있습니다.
                  <br />
                  승인되면 알림 없이 프로 스튜디오에 바로 입장할 수 있어요.
                </p>

                <Button
                  onClick={handleGoToProfile}
                  className="rounded-full h-12 text-base font-medium bg-[#A1E3D8] hover:bg-[#8DD3C7] text-gray-900 px-8 flex items-center gap-2 mx-auto"
                >
                  내 프로필로 이동
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
